import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, GitCompareArrows, Sparkles, Loader2, BarChart3 } from "lucide-react";
import { API } from "../App";

/**
 * MhM Next-Step Choice Page.
 *
 * Linked from the "Click here to revisit MyHomeMatrix" CTA inside both
 * the Clarity Report (`/mhm/report/:sessionId`) and the Project Fit
 * Report (`/mhm/comparison-report/:sessionId`).
 *
 * Shows three options:
 *   1. View my Clarity Report
 *   2. Compare with Projects (Project Fit)
 *   3. Start a fresh assessment
 *
 * Route: `/mhm/next/:sessionId`
 */
const MhMNextStepPage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/mhm/sessions/${sessionId}`);
        setSession(r.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "Session not found" : "Could not load your MhM session");
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center" data-testid="mhm-next-loading">
        <div className="text-center">
          <Loader2 className="w-7 h-7 animate-spin text-[#87A96B] mx-auto" />
          <p className="text-sm text-[#6D6660] mt-3">Loading your MhM session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4" data-testid="mhm-next-error">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 rounded-2xl bg-[#F0F5EC] flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-7 h-7 text-[#87A96B]" />
          </div>
          <h2 className="text-lg font-bold text-[#2C3E50] mb-2">Session not available</h2>
          <p className="text-sm text-[#6D6660] mb-5">{error}. You can start a fresh MyHomeMatrix assessment instead.</p>
          <a
            href="/mhm/start"
            data-testid="mhm-next-fresh-fallback"
            className="inline-flex items-center gap-2 bg-[#87A96B] hover:bg-[#76955d] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
          >
            Start MyHomeMatrix
          </a>
        </div>
      </div>
    );
  }

  const leadParam = session?.lead_id ? `?lead=${session.lead_id}` : "";
  const hasScoredProjects = (session?.projects || []).some((p) => p.fit_score != null);

  const cards = [
    {
      key: "report",
      icon: FileText,
      title: "View My Clarity Report",
      desc: "Go back to your home-buying priority report.",
      href: `/mhm/report/${sessionId}`,
      cta: "Open Report",
      tone: "bg-[#F0F5EC] border-[#D4E0CC] text-[#5A7A4A]",
      testId: "mhm-next-report",
    },
    {
      key: "compare",
      icon: GitCompareArrows,
      title: "Compare with Projects",
      desc: hasScoredProjects
        ? "Manage your shortlist — add more homes, edit ratings, or re-run the comparison."
        : "Add the homes you're considering, rate them on your priorities, and see how they compare.",
      href: `/mhm/projects/${sessionId}`,
      cta: hasScoredProjects ? "Manage & Compare →" : "Add & Compare Homes →",
      tone: "bg-[#FBF3EE] border-[#E8D4C3] text-[#8C5A38] hover:shadow-md hover:-translate-y-0.5",
      testId: "mhm-next-compare",
      disabled: false,
    },
    {
      key: "fresh",
      icon: Sparkles,
      title: "Start a Fresh Assessment",
      desc: "Re-take MyHomeMatrix from scratch — useful if your priorities have shifted.",
      href: `/mhm/start${leadParam}`,
      cta: "Take it Again",
      tone: "bg-[#EAF0F0] border-[#C8DAD9] text-[#3D6A6A]",
      testId: "mhm-next-fresh",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="mhm-next-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E8E3DE]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#87A96B] flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#2C3E50]">MyHomeMatrix</p>
            <p className="text-xs text-[#8B8580]">What would you like to do next?</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-[#2C3E50] font-['Manrope']">Welcome back.</h1>
          <p className="text-sm text-[#6D6660] mt-2">
            Pick how you'd like to continue with your MyHomeMatrix session.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cards.map((c) => {
            const Icon = c.icon;
            const baseClasses = `block rounded-2xl border-2 p-5 transition-all ${c.tone}`;
            const inner = (
              <>
                <div className="w-11 h-11 rounded-xl bg-white/70 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-base font-bold text-[#2C3E50]">{c.title}</p>
                <p className="text-xs text-[#6D6660] mt-1.5 leading-relaxed min-h-[36px]">{c.desc}</p>
                <p className="text-sm font-semibold mt-4">{c.cta}{c.disabled ? "" : (c.cta.endsWith("→") ? "" : " →")}</p>
              </>
            );
            return c.disabled ? (
              <div key={c.key} data-testid={c.testId} className={baseClasses} aria-disabled="true">
                {inner}
              </div>
            ) : (
              <a key={c.key} href={c.href} data-testid={c.testId} className={baseClasses}>
                {inner}
              </a>
            );
          })}
        </div>

        <p className="text-center text-[11px] text-[#8B8580] mt-8">
          Session ID: <span className="font-mono">{sessionId}</span>
        </p>
      </div>
    </div>
  );
};

export default MhMNextStepPage;
